import { useCallback, useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { apiFetch } from '@/lib/api'
import { useSpecialtyConfig } from '@/lib/hooks/useSpecialtyConfig'
import { useCatalogueContext } from '@/lib/catalogue-context'

const API = import.meta.env.VITE_API_URL ?? 'http://localhost:3001'
const RADIUS_OPTIONS = [20, 50, 100, 200]

interface DomainPublic {
  id: string
  slug: string
  label: string
  icon: string
  description: string
  featured: boolean
  specialties: { specialty: string; label: string; shortLabel: string; icon: string; subcategory: string | null; keywords: string[] }[]
}

interface Suggestion { key: string; label: string; icon: string; subcategory: string | null; domainSlug: string; domainLabel: string; domainIcon: string; haystack: string }

interface Expert {
  id: string
  firstName: string
  lastInitial: string
  avatarUrl: string | null
  bio: string | null
  rating: number
  ratingCount: number
  isVerified: boolean
  isPremium: boolean
  specialties: string[]
  missionsDone: number
  distanceKm: number | null
}

interface SearchResult {
  items: Expert[]
  total: number
  center: { lat: number; lng: number; label: string } | null
  radiusKm: number
  matchedSpecialties: string[]
  cityNotFound: boolean
}

interface Filters { q: string; domain: string; specialty: string; city: string; radiusKm: number }

function readFilters(): Filters {
  const p = new URLSearchParams(window.location.search)
  const r = Number(p.get('radiusKm'))
  return {
    q: p.get('q') ?? '',
    domain: p.get('domain') ?? '',
    specialty: p.get('specialty') ?? '',
    city: p.get('city') ?? '',
    radiusKm: RADIUS_OPTIONS.includes(r) ? r : 50,
  }
}

function toQuery(f: Filters): string {
  const p = new URLSearchParams()
  if (f.q) p.set('q', f.q)
  if (f.domain) p.set('domain', f.domain)
  if (f.specialty) p.set('specialty', f.specialty)
  if (f.city) { p.set('city', f.city); p.set('radiusKm', String(f.radiusKm)) }
  return p.toString()
}

function idFromToken(): string | null {
  try {
    const token = localStorage.getItem('velink_token')
    if (!token) return null
    const payload = JSON.parse(atob(token.split('.')[1]))
    return payload.exp * 1000 > Date.now() ? payload.sub : null
  } catch { return null }
}

function roleFromToken(): 'COMPANY' | 'PROVIDER' | 'ADMIN' | null {
  try {
    const token = localStorage.getItem('velink_token')
    if (!token) return null
    const payload = JSON.parse(atob(token.split('.')[1]))
    return payload.exp * 1000 > Date.now() ? payload.role : null
  } catch { return null }
}

function Stars({ rating }: { rating: number }) {
  const full = Math.round(rating)
  return <span className="text-warning text-xs tracking-tight">{'★'.repeat(full)}{'☆'.repeat(5 - full)}</span>
}

export default function ExpertsPage() {
  const { hasNav } = useCatalogueContext()
  const { badge } = useSpecialtyConfig()
  const [domains, setDomains] = useState<DomainPublic[]>([])
  const [filters, setFilters] = useState<Filters | null>(null)
  const [result, setResult] = useState<SearchResult | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [role, setRole] = useState<ReturnType<typeof roleFromToken>>(null)
  const [qText, setQText] = useState('')
  const [qFocus, setQFocus] = useState(false)
  const [askingId, setAskingId] = useState<string | null>(null)
  const myId = idFromToken()

  useEffect(() => {
    const initial = readFilters()
    setFilters(initial)
    setQText(initial.q)
    setRole(roleFromToken())
    apiFetch<DomainPublic[]>('/public/domains').then(setDomains).catch(() => {})
  }, [])

  const search = useCallback(async (f: Filters) => {
    setLoading(true)
    setError('')
    const qs = toQuery(f)
    window.history.replaceState(null, '', qs ? `/experts?${qs}` : '/experts')
    try {
      setResult(await apiFetch<SearchResult>(`/public/experts${qs ? `?${qs}` : ''}`))
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Erreur')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => { if (filters) search(filters) }, [filters, search])

  const currentDomain = useMemo(() => domains.find((d) => d.slug === filters?.domain) ?? null, [domains, filters?.domain])

  // Options du sélecteur de spécialité, groupées par sous-catégorie (domaine choisi) ou par domaine
  const specialtyGroups = useMemo(() => {
    const groups = new Map<string, DomainPublic['specialties']>()
    if (currentDomain) {
      for (const s of currentDomain.specialties) {
        const k = s.subcategory ?? 'Autres'
        groups.set(k, [...(groups.get(k) ?? []), s])
      }
    } else {
      for (const d of domains) groups.set(`${d.icon} ${d.label}`, d.specialties)
    }
    return [...groups.entries()]
  }, [domains, currentDomain])

  // Suggestions au fil de la frappe : libellés, sous-catégorie, domaine et mots-clés (synonymes) des spécialités
  const suggestions = useMemo<Suggestion[]>(() => {
    const t = qText.trim().toLowerCase()
    if (t.length < 2) return []
    const all: Suggestion[] = domains.flatMap((d) => d.specialties.map((s) => ({
      key: s.specialty, label: s.label, icon: s.icon, subcategory: s.subcategory,
      domainSlug: d.slug, domainLabel: d.label, domainIcon: d.icon,
      haystack: [s.label, s.shortLabel, s.subcategory ?? '', d.label, ...(s.keywords ?? [])].join(' ').toLowerCase(),
    })))
    return all.filter((s) => s.haystack.includes(t)).slice(0, 8)
  }, [domains, qText])

  function update(patch: Partial<Filters>) {
    setFilters((f) => ({ ...(f ?? readFilters()), ...patch }))
  }

  function onSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault()
    const fd = new FormData(e.currentTarget)
    update({ q: String(fd.get('q') ?? '').trim(), city: String(fd.get('city') ?? '').trim() })
  }

  const missionHref = (expert: Expert) => {
    const spec = expert.specialties.find((s) => result?.matchedSpecialties.includes(s)) ?? expert.specialties[0]
    const target = `/missions/nouvelle?specialty=${encodeURIComponent(spec ?? '')}&provider=${expert.id}`
    if (role === 'COMPANY') return target
    if (role === 'PROVIDER' || role === 'ADMIN') return target
    return `/inscription/entreprise?next=${encodeURIComponent(target)}`
  }

  async function askQuestion(expert: Expert) {
    if (!role) {
      window.location.href = `/connexion?next=${encodeURIComponent('/experts')}`
      return
    }
    if (role === 'ADMIN') {
      setError('Un compte administrateur ne peut pas poser de question — connectez-vous avec un compte entreprise ou prestataire.')
      return
    }
    setAskingId(expert.id)
    try {
      const { url } = await apiFetch<{ url: string }>(`/consultations/${expert.id}`, { method: 'POST' })
      window.location.href = url
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Impossible de démarrer la consultation')
      setAskingId(null)
    }
  }

  return (
    <div className="min-h-screen" style={hasNav ? undefined : { background: 'linear-gradient(135deg, #0A0A14 0%, #0D0D2B 50%, #0A0A14 100%)' }}>
      {!hasNav && (
        <header className="border-b border-white/[0.08] px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2.5">
            <div className="w-[30px] h-[30px] rounded-lg bg-accent/20 border border-accent/30 flex items-center justify-center">
              <span className="text-accent-hover font-bold text-sm">V</span>
            </div>
            <span className="font-semibold text-text-primary text-[17px] tracking-tight">Velink</span>
          </Link>
          <div className="flex gap-3">
            <Link to="/connexion" className="glass-btn px-4 py-2 text-sm rounded-xl">Connexion</Link>
            <Link to="/inscription/entreprise" className="glass-btn-accent px-4 py-2 text-sm rounded-xl">Publier une mission</Link>
          </div>
        </header>
      )}

      <div className="max-w-6xl mx-auto px-6 py-12">
        <div className="text-center mb-10">
          <h1 className="text-4xl font-bold text-text-primary mb-3">Trouver un expert</h1>
          <p className="text-text-secondary text-lg">
            Des prestataires qualifiés, notés par les entreprises, disponibles près de chez vous.
          </p>
        </div>

        {/* Recherche */}
        <form onSubmit={onSubmit} className="glass-card p-5 mb-5">
          <div className="flex flex-col md:flex-row gap-3">
            <div className="flex-1 relative">
              <label className="text-text-muted text-xs font-medium block mb-1.5">De quel expert avez-vous besoin ?</label>
              <input
                name="q"
                value={qText}
                onChange={(e) => setQText(e.target.value)}
                onFocus={() => setQFocus(true)}
                onBlur={() => setQFocus(false)}
                autoComplete="off"
                placeholder="SSIAP, chauffeur VTC, formateur SST, permis C…"
                className="glass-input w-full text-sm"
              />
              {qFocus && suggestions.length > 0 && (
                <ul className="absolute z-20 left-0 right-0 mt-1 glass-card-dark rounded-xl overflow-hidden shadow-xl">
                  {suggestions.map((s) => (
                    <li key={s.key}>
                      <button
                        type="button"
                        onMouseDown={(e) => { e.preventDefault(); setQText(''); update({ q: '', domain: s.domainSlug, specialty: s.key }) }}
                        className="w-full text-left px-3 py-2 flex items-center gap-2 hover:bg-white/[0.06] transition-colors"
                      >
                        <span>{s.icon}</span>
                        <span className="text-sm text-text-primary flex-1 truncate">{s.label}</span>
                        <span className="text-[11px] text-text-muted truncate">{s.domainIcon} {s.domainLabel}{s.subcategory ? ` · ${s.subcategory}` : ''}</span>
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
            <div className="md:w-56">
              <label className="text-text-muted text-xs font-medium block mb-1.5">Ville</label>
              <input name="city" defaultValue={filters?.city ?? ''} key={`c-${filters?.city ?? ''}`} placeholder="ex. Lyon" className="glass-input w-full text-sm" />
            </div>
            <div className="md:w-36">
              <label className="text-text-muted text-xs font-medium block mb-1.5">Rayon</label>
              <select value={filters?.radiusKm ?? 50} onChange={(e) => update({ radiusKm: Number(e.target.value) })} className="glass-input w-full text-sm">
                {RADIUS_OPTIONS.map((r) => <option key={r} value={r}>{r} km</option>)}
              </select>
            </div>
            <div className="flex items-end">
              <button type="submit" className="glass-btn-accent px-6 py-2.5 text-sm rounded-xl w-full md:w-auto">Rechercher</button>
            </div>
          </div>

          <div className="flex flex-col md:flex-row gap-3 mt-3">
            <div className="flex-1">
              <label className="text-text-muted text-xs font-medium block mb-1.5">Domaine</label>
              <select value={filters?.domain ?? ''} onChange={(e) => update({ domain: e.target.value, specialty: '' })} className="glass-input w-full text-sm">
                <option value="">Tous les domaines</option>
                {domains.map((d) => <option key={d.id} value={d.slug}>{d.icon} {d.label}</option>)}
              </select>
            </div>
            <div className="flex-1">
              <label className="text-text-muted text-xs font-medium block mb-1.5">Spécialité</label>
              <select value={filters?.specialty ?? ''} onChange={(e) => update({ specialty: e.target.value })} className="glass-input w-full text-sm">
                <option value="">Toutes les spécialités</option>
                {specialtyGroups.map(([group, items]) => (
                  <optgroup key={group} label={group}>
                    {items.map((s) => <option key={s.specialty} value={s.specialty}>{s.icon} {s.label}</option>)}
                  </optgroup>
                ))}
              </select>
            </div>
          </div>
        </form>

        {/* Raccourcis domaines */}
        {domains.length > 0 && (
          <div className="flex gap-2 flex-wrap mb-8">
            <button onClick={() => update({ domain: '', specialty: '' })} className={['px-3 py-1.5 rounded-full text-xs border transition-all', !filters?.domain ? 'bg-accent/15 border-accent/30 text-accent' : 'glass-btn'].join(' ')}>Tous</button>
            {domains.map((d) => (
              <button key={d.id} onClick={() => update({ domain: d.slug, specialty: '' })} className={['px-3 py-1.5 rounded-full text-xs border transition-all', filters?.domain === d.slug ? 'bg-accent/15 border-accent/30 text-accent' : 'glass-btn'].join(' ')}>
                {d.icon} {d.label}
              </button>
            ))}
          </div>
        )}

        {error && <div className="mb-6 px-4 py-3 rounded-xl bg-danger/10 border border-danger/25 text-danger text-sm">{error}</div>}
        {result?.cityNotFound && (
          <div className="mb-6 px-4 py-3 rounded-xl bg-warning/10 border border-warning/25 text-warning text-sm">
            Ville « {filters?.city} » introuvable — résultats affichés sans filtre de distance.
          </div>
        )}

        {/* Résultats */}
        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
            {[1, 2, 3, 4, 5, 6].map((i) => <div key={i} className="glass-card h-52 animate-pulse" />)}
          </div>
        ) : !result || result.items.length === 0 ? (
          <div className="glass-card p-14 text-center">
            <p className="text-5xl mb-4">🔍</p>
            <p className="text-text-primary font-semibold text-lg mb-2">Aucun expert pour ces critères</p>
            <p className="text-text-secondary text-sm max-w-md mx-auto mb-6">
              Élargissez le rayon ou le domaine — ou publiez votre mission : les prestataires qualifiés à proximité seront notifiés instantanément.
            </p>
            <Link to={role === 'COMPANY' ? '/missions/nouvelle' : '/inscription/entreprise'} className="glass-btn-accent px-6 py-3 text-sm rounded-xl inline-block">
              Publier une mission →
            </Link>
          </div>
        ) : (
          <>
            <p className="text-text-muted text-xs mb-4">
              {result.total} expert{result.total > 1 ? 's' : ''}
              {result.center ? ` à moins de ${result.radiusKm} km de ${result.center.label}` : ''}
              {currentDomain ? ` · ${currentDomain.label}` : ''}
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
              {result.items.map((e) => (
                <article key={e.id} className="glass-card p-5 flex flex-col gap-3">
                  <div className="flex items-start gap-3">
                    <div className="w-14 h-14 rounded-2xl bg-white/[0.08] border border-white/[0.12] overflow-hidden flex items-center justify-center text-text-secondary font-bold text-lg flex-shrink-0">
                      {e.avatarUrl
                        ? <img src={`${API}${e.avatarUrl}`} alt="" className="w-full h-full object-cover" />
                        : `${e.firstName[0] ?? ''}${e.lastInitial[0] ?? ''}`.toUpperCase()}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-1.5 flex-wrap">
                        <h2 className="text-text-primary font-semibold">{e.firstName} {e.lastInitial}</h2>
                        {e.isVerified && <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-success/15 border border-success/30 text-success" title="Documents vérifiés par Velink">✓ Vérifié</span>}
                        {e.isPremium && <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-warning/15 border border-warning/30 text-warning">★ Premium</span>}
                      </div>
                      <div className="flex items-center gap-2 mt-0.5 text-xs text-text-muted">
                        {e.ratingCount > 0 ? <><Stars rating={e.rating} /><span>{e.rating.toFixed(1)} ({e.ratingCount})</span></> : <span>Nouveau sur Velink</span>}
                        {e.missionsDone > 0 && <span>· {e.missionsDone} mission{e.missionsDone > 1 ? 's' : ''}</span>}
                      </div>
                      {e.distanceKm !== null && <p className="text-success text-xs mt-0.5">📍 à {e.distanceKm} km</p>}
                    </div>
                  </div>

                  {e.bio && <p className="text-text-secondary text-sm leading-relaxed line-clamp-3">{e.bio}</p>}

                  <div className="flex gap-1.5 flex-wrap">
                    {e.specialties.slice(0, 5).map((s) => {
                      const b = badge(s)
                      const hit = result.matchedSpecialties.includes(s)
                      return (
                        <span key={s} className={`text-[11px] px-2 py-0.5 rounded-full border ${b.badgeClass} ${hit ? 'ring-1 ring-accent/60' : ''}`} title={b.label}>
                          {b.icon} {b.shortLabel}
                        </span>
                      )
                    })}
                    {e.specialties.length > 5 && <span className="text-[11px] text-text-muted">+{e.specialties.length - 5}</span>}
                  </div>

                  <div className="flex gap-2 mt-auto">
                    {e.id !== myId && (
                      <button
                        type="button"
                        onClick={() => askQuestion(e)}
                        disabled={askingId === e.id}
                        className="glass-btn px-4 py-2.5 text-sm rounded-xl text-center flex-1"
                      >
                        {askingId === e.id ? '…' : 'Poser une question'}
                      </button>
                    )}
                    <Link to={missionHref(e)} className="glass-btn-accent px-4 py-2.5 text-sm rounded-xl text-center flex-1">
                      Proposer une mission →
                    </Link>
                  </div>
                </article>
              ))}
            </div>
          </>
        )}

        <p className="text-text-muted text-xs text-center mt-10">
          Les coordonnées des experts ne sont jamais publiées : la mise en relation, le contrat et le paiement passent par Velink.
        </p>
      </div>
    </div>
  )
}
